import { ChangeEvent, FocusEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { getDobFieldErrors } from '@/utils/dob'

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`
const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`
const StyledInput = styled.input`
  display: flex;
  padding: 0px;
  width: 100%;
  margin-top: 10px;
  padding-bottom: 4px;
  background-color: transparent;
  border: none;
  border-bottom: 2px solid var(--rock);
  height: 32px;
  font-family: 'PP Object Sans';
  font-size: 16px;
  text-align: center;

  &:focus {
    outline: none;
    border-bottom-color: var(--clementine);
    & + label {
      top: -10px;
      font-size: 12px;
      color: var(--clementine);
    }
  }

  &:not(:placeholder-shown) + label,
  &:focus + label {
    top: -2px;
    font-size: 12px;
    color: var(--clementine);
  }

  .dark & {
    &:focus {
      border-bottom-color: var(--sun);
      & + label {
        color: var(--sun);
      }
    }
    &:not(:placeholder-shown) + label,
    &:focus + label {
      color: var(--sun);
    }
  }
`
const Label = styled.label`
  position: absolute;
  left: 0;
  top: 10px; /* Initial position */
  transition: all 0.2s ease-in-out; /* Smooth transition */
  color: #9ca3af;
`
const ErrorText = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #dc2626;
`
type BirthdayField = 'year' | 'month' | 'day'

interface BirthdayInputProps {
  value: string
  onChange: (date: string) => void
  onBlur?: () => void
  showFieldErrors?: boolean
}
export const BirthdayInput = ({
  value,
  onChange,
  onBlur,
  showFieldErrors = false,
}: BirthdayInputProps) => {
  const { t } = useTranslation()
  const yearRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLInputElement>(null)
  const dayRef = useRef<HTMLInputElement>(null)
  const [touched, setTouched] = useState({
    year: false,
    month: false,
    day: false,
  })
  const [storedYear, storedMonth, storedDay] = value
    ? value.split('-')
    : ['', '', '']

  const errorKeys = getDobFieldErrors(value)
  const yearError =
    showFieldErrors && touched.year && errorKeys.year ? t(errorKeys.year) : null
  const monthError =
    showFieldErrors && touched.month && errorKeys.month
      ? t(errorKeys.month)
      : null
  const dayError =
    showFieldErrors && touched.day && errorKeys.day ? t(errorKeys.day) : null
  const dateError =
    showFieldErrors && touched.day && errorKeys.date ? t(errorKeys.date) : null

  const markTouched = (field: BirthdayField) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }))
    onBlur?.()
  }

  const handleBlur = (
    field: BirthdayField,
    e: FocusEvent<HTMLInputElement>
  ) => {
    const digits = e.target.value.replace(/\D/g, '')
    if (field === 'month' && /^\d$/.test(digits)) {
      updateFullDate(storedYear, `0${digits}`, storedDay)
    } else if (field === 'day' && /^\d$/.test(digits)) {
      updateFullDate(storedYear, storedMonth, `0${digits}`)
    }
    markTouched(field)
  }

  const updateFullDate = (year: string, month: string, day: string) => {
    const cleanYear = year.replace(/\D/g, '')
    const cleanMonth = month.replace(/\D/g, '')
    const cleanDay = day.replace(/\D/g, '')
    if (cleanYear || cleanMonth || cleanDay) {
      onChange(`${cleanYear}-${cleanMonth}-${cleanDay}`)
    } else {
      onChange('')
    }
  }
  const handleYearChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newYear = e.target.value.replace(/\D/g, '').slice(0, 4)
    updateFullDate(newYear, storedMonth, storedDay)
    if (newYear.length === 4) {
      monthRef.current?.focus()
    }
  }
  const handleMonthChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newMonth = e.target.value.replace(/\D/g, '').slice(0, 2)
    if (newMonth.length === 1 && Number(newMonth) > 1) {
      newMonth = `0${newMonth}`
    }
    updateFullDate(storedYear, newMonth, storedDay)
    if (newMonth.length === 2) {
      dayRef.current?.focus()
    }
  }
  const handleDayChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newDay = e.target.value.replace(/\D/g, '').slice(0, 2)
    if (newDay.length === 1 && Number(newDay) > 3) {
      newDay = `0${newDay}`
    }
    updateFullDate(storedYear, storedMonth, newDay)
  }

  const handleLabelClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    inputRef.current?.focus()
  }
  return (
    <div>
      <InputContainer data-testid="input-form-field-birthday">
        <InputWrapper>
          <StyledInput
            ref={yearRef}
            type="text"
            inputMode="numeric"
            placeholder=" "
            value={storedYear}
            onChange={handleYearChange}
            onBlur={(e) => handleBlur('year', e)}
            data-testid="input-form-field-birthday-year"
          />
          <Label onClick={() => handleLabelClick(yearRef)}>YYYY</Label>
          {yearError ? (
            <ErrorText role="alert" data-testid="error-birthday-year">
              {yearError}
            </ErrorText>
          ) : null}
        </InputWrapper>
        <InputWrapper>
          <StyledInput
            ref={monthRef}
            type="text"
            inputMode="numeric"
            placeholder=" "
            value={storedMonth}
            onChange={handleMonthChange}
            onBlur={(e) => handleBlur('month', e)}
            data-testid="input-form-field-birthday-month"
          />
          <Label onClick={() => handleLabelClick(monthRef)}>MM</Label>
          {monthError ? (
            <ErrorText role="alert" data-testid="error-birthday-month">
              {monthError}
            </ErrorText>
          ) : null}
        </InputWrapper>
        <InputWrapper>
          <StyledInput
            ref={dayRef}
            type="text"
            inputMode="numeric"
            placeholder=" "
            value={storedDay}
            onChange={handleDayChange}
            onBlur={(e) => handleBlur('day', e)}
            data-testid="input-form-field-birthday-day"
          />
          <Label onClick={() => handleLabelClick(dayRef)}>DD</Label>
          {dayError ? (
            <ErrorText role="alert" data-testid="error-birthday-day">
              {dayError}
            </ErrorText>
          ) : null}
        </InputWrapper>
      </InputContainer>
      {dateError ? (
        <ErrorText role="alert" data-testid="error-birthday-date">
          {dateError}
        </ErrorText>
      ) : null}
    </div>
  )
}
